import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, School } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";

export default function Gurus() {
  const { meta, setFollowing } = useAuth();
  const [gurus, setGurus] = useState([]);
  const [specialty, setSpecialty] = useState("All");
  const [followingIds, setFollowingIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());

  const load = async () => {
    try {
      const params = specialty === "All" ? {} : { params: { specialty } };
      const { data } = await api.get("/gurus", params);
      setGurus(data);
      setFollowingIds(new Set(data.filter((g) => g.followed_by_me).map((g) => g.id)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [specialty]);

  const toggle = async (g) => {
    if (pendingIds.has(g.id)) return;
    const wasFollowing = followingIds.has(g.id);
    setPendingIds((s) => { const n = new Set(s); n.add(g.id); return n; });
    setFollowingIds((s) => {
      const n = new Set(s);
      if (wasFollowing) n.delete(g.id); else n.add(g.id);
      return n;
    });
    try {
      if (wasFollowing) await api.delete(`/users/${g.id}/follow`);
      else { await api.post(`/users/${g.id}/follow`); toast.success(`Following ${g.full_name}`); }
      setFollowing(g.id, !wasFollowing);
    } catch (e) {
      setFollowingIds((s) => {
        const n = new Set(s);
        if (wasFollowing) n.add(g.id); else n.delete(g.id);
        return n;
      });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(g.id); return n; });
    }
  };

  return (
    <div data-testid="gurus-page" className="max-w-6xl mx-auto space-y-6">
      <div>
        <div className="label-eyebrow inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Guru Directory
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)] mt-2">Learn from senior Vaidyas & researchers</h1>
        <p className="text-[color:var(--ayur-ink-2)] mt-2 max-w-2xl">
          A curated list of teachers, clinicians and researchers shaping contemporary Ayurveda. Follow with one tap — their posts will show up in your feed.
        </p>
      </div>

      <div className="paper-card p-4 flex flex-wrap items-center gap-3">
        <span className="label-eyebrow">Filter by specialty</span>
        <Select value={specialty} onValueChange={setSpecialty}>
          <SelectTrigger data-testid="guru-specialty-filter" className="bg-white w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All specialties</SelectItem>
            {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {gurus.length === 0 && (
          <div className="col-span-full paper-card p-12 text-center">
            <div className="label-eyebrow">Empty</div>
            <h3 className="font-serif-display text-2xl mt-2">No gurus in this specialty yet</h3>
          </div>
        )}
        {gurus.map((g) => (
          <div key={g.id} data-testid="guru-card" className="paper-card p-6 flex flex-col">
            <div className="flex items-start gap-3">
              <Link to={`/app/scholars/${g.id}`}>
                <ScholarAvatar user={g} size={56} />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/app/scholars/${g.id}`} className="font-serif-display text-lg font-semibold text-[color:var(--ayur-ink)] link-underline inline-flex items-center gap-1">
                  {g.full_name}
                  {g.verified && <VerifiedBadge size={14} />}
                </Link>
                <div className="text-xs text-[color:var(--ayur-terracotta)] font-semibold mt-0.5">{g.specialty}</div>
                <div className="text-xs text-[color:var(--ayur-mute)] mt-1 flex items-start gap-1">
                  <School className="w-3 h-3 mt-0.5 shrink-0" /> {g.college}
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[color:var(--ayur-ink-2)] leading-relaxed line-clamp-4 flex-1">{g.bio}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-[color:var(--ayur-mute)]">{(() => { const n = g.followers_count + (followingIds.has(g.id) ? 1 : 0) - (g.followed_by_me ? 1 : 0); return `${n} follower${n === 1 ? "" : "s"}`; })()}</span>
              <Button
                size="sm"
                data-testid="follow-guru-button"
                onClick={() => toggle(g)}
                disabled={pendingIds.has(g.id)}
                className={followingIds.has(g.id)
                  ? "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-badge)]"
                  : "bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"}
              >
                {pendingIds.has(g.id) ? "…" : (followingIds.has(g.id) ? "Following" : "Follow")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
